import React, { useCallback, useRef } from 'react'
import { $generateNodesFromDOM } from '@lexical/html'
import { $getRoot } from 'lexical'
import { Box, Typography } from '@mui/material'
import Editor from './editor/Editor'

export default function LexicalBlogEditor({
  value,
  editorStateJson,
  onChange,
  onEditorStateJsonChange,
  onUploadImage,
  isUploadingImage,
  disabled,
  onNotify,
  contentError,
  contentStatusMessage,
}) {
  const initialHtmlRef = useRef(value)
  const initialEditorStateJsonRef = useRef(editorStateJson)

  const editorStateInit = useCallback((editor) => {
    const html = initialHtmlRef.current
    if (!html) return
    const parser = new DOMParser()
    const dom = parser.parseFromString(html, 'text/html')

    const INLINE_STYLE_SELECTOR = 'span[style], strong[style], em[style], code[style], mark[style], sub[style], sup[style]';
    dom.querySelectorAll(INLINE_STYLE_SELECTOR).forEach((span) => {
      const s = span.style
      const parts = []
      if (s.fontFamily) parts.push(`font-family: ${s.fontFamily}`)
      if (s.fontSize) parts.push(`font-size: ${s.fontSize}`)
      if (s.color) parts.push(`color: ${s.color}`)
      if (s.backgroundColor) parts.push(`background-color: ${s.backgroundColor}`)
      if (parts.length > 0) {
        span.setAttribute('data-lex-style', parts.join('; '))
      }
    })

    const nodes = $generateNodesFromDOM(editor, dom)
    const root = $getRoot()
    root.clear()
    if (nodes.length > 0) {
      root.append(...nodes)
    }
  }, [])

  const effectiveEditorState = initialEditorStateJsonRef.current
    ? JSON.stringify(initialEditorStateJsonRef.current)
    : editorStateInit

  const handleHtmlChange = useCallback(
    (html) => {
      if (onChange) {
        onChange(html)
      }
    },
    [onChange],
  )

  return (
    <Box
      sx={{
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <Editor
        editorState={effectiveEditorState}
        onHtmlChange={handleHtmlChange}
        onEditorStateJsonChange={onEditorStateJsonChange}
        onUploadImage={onUploadImage}
      />

      {contentError && (
        <Typography
          variant='caption'
          sx={{ color: 'error.main', mt: 0.5, display: 'block' }}
        >
          {contentError}
        </Typography>
      )}

      {contentStatusMessage && !contentError && (
        <Typography
          variant='caption'
          sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}
        >
          {contentStatusMessage}
        </Typography>
      )}

      {isUploadingImage && (
        <Typography
          variant='caption'
          sx={{ color: 'info.main', mt: 0.5, display: 'block' }}
        >
          Uploading image...
        </Typography>
      )}
    </Box>
  )
}
